import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { categoriesApi, productsApi, translationJobsApi, unitsApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import Drawer from '@/components/common/Drawer'
import Confirm from '@/components/common/Confirm'
import Icon from '@/components/common/Icons'
import TamilField from '@/components/common/TamilField'
import BulkTranslate from '@/components/common/BulkTranslate'
import ProductImport from '@/components/products/ProductImport'
import { saveBlob } from '@/lib/download'
import {
  Alert,
  Empty,
  ErrorNote,
  Loading,
  PageHeader,
  Pagination,
  Toolbar,
} from '@/components/common/Ui'
import { money } from '@/lib/format'
import { PERMS, can, useAuth } from '@/store/auth'

const PAGE_SIZE = 50
const TIERS = ['A', 'A+', 'B', 'B+', 'P']

// Hidden, not removed. The match tester still exists and still works — its
// page is MatchTester.jsx and its route is /products/match-tester, both
// untouched — it is just not offered from this header any more. Flip this
// back to true to put the button back.
const SHOW_MATCH_TESTER = false

// Hidden the same way: the bulk-translate buttons in the header and the row
// checkboxes that feed them. The BulkTranslate drawer and the pending counts
// are untouched.
const SHOW_BULK_TRANSLATE = false

// A new product, as the form starts it. No id: that is what tells the save
// whether it is creating or editing, and what unlocks the item code.
const BLANK = {
  item_code: '',
  item_name: '',
  item_name_ta: '',
  category: '',
  hsn: '',
  tax_rate: '18',
  inclusive_of_tax: false,
  base_unit: 'KILOGRAMS',
  secondary_unit: '',
  is_active: true,
  prices: Object.fromEntries(TIERS.map((t) => [t, ''])),
}

export default function Products() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const editable = can(user, PERMS.productsWrite)
  // The product being edited, as a form draft. item_code is absent on
  // purpose: it is the catalog's key and the importer matches on it.
  const [draft, setDraft] = useState(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  // Ticked rows, by id, and whether the list is narrowed to untranslated
  // ones. The catalog is paginated, so 'all pending' cannot mean 'every
  // ticked box on this page'.
  const [picked, setPicked] = useState({})
  const [onlyPending, setOnlyPending] = useState(false)
  const [bulk, setBulk] = useState(null)   // null | { ids } | { all: true }
  const [confirming, setConfirming] = useState(null)
  const [importing, setImporting] = useState(false)
  const [exporting, setExporting] = useState('')
  // The filter lives in the URL so the Categories screen can link straight to
  // "the products in this category", and so a filtered view can be shared.
  const [urlParams, setUrlParams] = useSearchParams()
  const category = urlParams.get('category') ?? ''
  const setCategory = (value) => setUrlParams(value ? { category: value } : {})

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: productsApi.categories,
  })

  // How many products have no Tamil at all — counted on the server, because
  // the browser only ever holds one page of them.
  const { data: pendingCounts } = useQuery({
    queryKey: ['translation-pending'],
    queryFn: translationJobsApi.pending,
    enabled: editable,
  })

  // The managed lists, so the form offers what the catalog already speaks in
  // rather than free text that would coin a new category on a typo.
  const { data: units } = useQuery({
    queryKey: ['units', { is_active: true }],
    queryFn: () => unitsApi.list({ is_active: true }),
    enabled: editable,
  })
  const { data: categoryRows } = useQuery({
    queryKey: ['category-management'],
    queryFn: categoriesApi.list,
    enabled: editable,
  })

  const params = { page, page_size: PAGE_SIZE }
  if (search) params.search = search
  if (category) params.category = category
  if (onlyPending) params.has_tamil = false

  // Whether anything is narrowing the list, so the count can say whether it
  // is "everything" or "everything matching".
  const narrowed = Boolean(search || category || onlyPending)

  // Exports the filters, not the page: the file is the list as filtered,
  // however many pages it runs to.
  const runExport = async (format) => {
    setExporting(format)
    setError('')
    try {
      const { page: _page, page_size: _size, ...filters } = params
      const { blob, filename } = await productsApi.export(filters, format)
      saveBlob(blob, filename)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setExporting('')
    }
  }

  const clearFilters = () => {
    setSearch('')
    setOnlyPending(false)
    setPage(1)
    // The category lives in the URL so a filtered view can be linked to —
    // including from the Categories screen — so clearing has to clear that
    // too, or a reload brings it straight back.
    setUrlParams({}, { replace: true })
  }

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['products', params],
    queryFn: () => productsApi.list(params),
  })

  const save = useMutation({
    mutationFn: ({ id, ...payload }) =>
      id ? productsApi.update(id, payload) : productsApi.create(payload),
    onSuccess: (product) => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      // A new product is one more row without Tamil, and the Products
      // screen counts those on its Translate button.
      queryClient.invalidateQueries({ queryKey: ['translation-pending'] })
      setDraft(null)
      setError('')
      setNotice(`Saved ${product.item_code}.`)
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const remove = useMutation({
    mutationFn: productsApi.remove,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['translation-pending'] })
      setError('')
      setNotice('Product deleted.')
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const edit = (product) => {
    setNotice('')
    setError('')
    setDraft({
      id: product.id,
      item_code: product.item_code,
      item_name: product.item_name,
      item_name_ta: product.item_name_ta ?? '',
      category: product.category ?? '',
      hsn: product.hsn ?? '',
      tax_rate: String(product.tax_rate ?? '18'),
      inclusive_of_tax: Boolean(product.inclusive_of_tax),
      base_unit: product.base_unit ?? 'KILOGRAMS',
      secondary_unit: product.secondary_unit ?? '',
      is_active: Boolean(product.is_active),
      prices: Object.fromEntries(
        TIERS.map((t) => [t, String(product.prices.find((p) => p.tier === t)?.rate ?? '')]),
      ),
    })
  }

  const submit = (event) => {
    event.preventDefault()
    const { id, item_code, prices, ...rest } = draft
    const payload = {
      ...rest,
      tax_rate: rest.tax_rate === '' ? undefined : rest.tax_rate,
      prices: Object.fromEntries(
        Object.entries(prices).filter(([, rate]) => rate !== ''),
      ),
    }
    // Only a new product carries its code: on an edit the API has no field
    // for it, because renaming it would orphan every order line that
    // already names it.
    if (!id) payload.item_code = item_code.trim()
    save.mutate({ id, ...payload })
  }

  const setField = (key, value) => setDraft((d) => ({ ...d, [key]: value }))

  // Guarded with ?? []: this page renders while the query is still in
  // flight rather than returning early, so `data` is undefined at first.
  const pageIds = (data?.items ?? []).map((p) => p.id)
  const chosenIds = Object.entries(picked)
    .filter(([, on]) => on)
    .map(([id]) => Number(id))

  const rateFor = (product, tier) => {
    const price = product.prices.find((p) => p.tier === tier)
    return price ? money(price.rate) : '—'
  }

  return (
    <div className="products-page">
      <PageHeader
        title="Products"
        // Counted for whatever is on screen: after narrowing to a category
        // or to the rows without Tamil, "how many is that?" is the next
        // question.
        subtitle={
          data
            ? `${data.total.toLocaleString()} product${data.total === 1 ? '' : 's'}` +
              (narrowed ? ' match these filters' : '')
            : 'Imported from the item master'
        }
        actions={
          <>
            {SHOW_BULK_TRANSLATE && editable && (pendingCounts?.product ?? 0) > 0 && (
              <button
                type="button"
                className="btn btn-sm"
                title="Every product with no Tamil yet, across every page"
                onClick={() => {
                  setNotice('')
                  setBulk({ all: true })
                }}
              >
                Translate all pending ({pendingCounts.product})
              </button>
            )}
            {SHOW_BULK_TRANSLATE && editable && (
              <button
                type="button"
                className="btn btn-sm"
                disabled={chosenIds.length === 0}
                title={
                  chosenIds.length === 0
                    ? 'Tick the products to translate'
                    : undefined
                }
                onClick={() => {
                  setNotice('')
                  setBulk({ ids: chosenIds })
                }}
              >
                Translate selected ({chosenIds.length})
              </button>
            )}
            {/* Categories and units live in the sidebar, under Products. */}
            {SHOW_MATCH_TESTER && (
              <Link to="/products/match-tester" className="btn btn-sm">
                Test the matcher
              </Link>
            )}
            {editable && (
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => {
                  setNotice('')
                  setError('')
                  setImporting(true)
                }}
              >
                Import
              </button>
            )}
            {editable && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setNotice('')
                  setError('')
                  setDraft({ ...BLANK, prices: { ...BLANK.prices } })
                }}
              >
                Add product
              </button>
            )}
          </>
        }
      />

      <Toolbar>
        <input
          type="search"
          placeholder="Name or item code…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
        />
        <select
          value={category}
          onChange={(e) => {
            setCategory(e.target.value)
            setPage(1)
          }}
        >
          <option value="">All categories</option>
          {categories?.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={onlyPending}
            onChange={(e) => {
              setOnlyPending(e.target.checked)
              setPage(1)
            }}
          />
          Only without Tamil
        </label>
        {narrowed && (
          <button
            type="button"
            className="btn btn-sm btn-icon"
            onClick={clearFilters}
            title="Clear all filters"
            aria-label="Clear all filters"
          >
            <Icon name="clearFilter" />
          </button>
        )}

        {/* A select, as on Orders: choosing a format is one decision. It
            returns to 'Export' afterwards so it reads as an action. */}
        <div className="toolbar-end">
          <select
            aria-label="Export the filtered products"
            className="export-select"
            value=""
            disabled={Boolean(exporting)}
            onChange={(e) => {
              if (e.target.value) runExport(e.target.value)
            }}
          >
            <option value="">{exporting ? 'Preparing…' : 'Export'}</option>
            <option value="xlsx">Excel</option>
            <option value="csv">CSV</option>
          </select>
        </div>
      </Toolbar>

      <ProductImport
        open={importing}
        onClose={() => setImporting(false)}
        onDone={(message) => {
          setImporting(false)
          setNotice(message)
        }}
      />

      <Alert tone="success" message={notice} onClose={() => setNotice('')} />
      {!draft && (
        <Alert tone="danger" message={error} onClose={() => setError('')} />
      )}

      {/* The same form and the same submit handler, moved out of the page
          flow: editing a product no longer pushes the catalog down the page.
          Wide, because this one has seven fields and five tier rates. */}
      <Drawer
        open={Boolean(draft)}
        error={error}
        onErrorDismiss={() => setError('')}
        size="wide"
        title={draft?.id ? 'Edit product' : 'Add product'}
        subtitle={
          draft?.id
            ? draft.item_name
            : 'Prices are per tier; leave a tier blank to price it later.'
        }
        onClose={() => setDraft(null)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setDraft(null)}>
              Cancel
            </button>
            <button
              type="submit"
              form="product-form"
              className="btn btn-primary"
              disabled={save.isPending}
            >
              {save.isPending ? 'Saving…' : draft?.id ? 'Save' : 'Add product'}
            </button>
          </>
        }
      >
        {draft && (
          <form id="product-form" onSubmit={submit}>
            {draft.id ? (
              /* The catalog's key and what the importer matches on, so it is
                 shown rather than offered once a product exists. */
              <div className="drawer-code">
                Item code <span className="mono">{draft.item_code}</span>
              </div>
            ) : (
              <label className="field">
                <span>Item code *</span>
                <input
                  value={draft.item_code}
                  onChange={(e) => setField('item_code', e.target.value)}
                  placeholder="MSWN2X12"
                  required
                  autoFocus
                />
                <span className="hint">
                  The catalog's key. The importer matches on it, so it cannot
                  be changed afterwards.
                </span>
              </label>
            )}
          <div className="form-grid">
            <label className="field">
              <span>Item name *</span>
              <input
                value={draft.item_name}
                onChange={(e) => setField('item_name', e.target.value)}
                required
              />
            </label>
            <TamilField
              value={draft.item_name_ta}
              onChange={(value) => setField('item_name_ta', value)}
              source={draft.item_name}
              entityType="product"
              label="Item name (Tamil)"
              hint="The name only. The item code, HSN and the sizes in the name stay as they are."
            />
            <label className="field">
              <span>Category</span>
              <select
                value={draft.category}
                onChange={(e) => setField('category', e.target.value)}
              >
                <option value="">— none —</option>
                {(categoryRows ?? []).map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>HSN</span>
              <input value={draft.hsn} onChange={(e) => setField('hsn', e.target.value)} />
            </label>
            <label className="field">
              <span>GST %</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={draft.tax_rate}
                onChange={(e) => setField('tax_rate', e.target.value)}
              />
            </label>
            <label className="field">
              <span>Base unit *</span>
              <select
                value={draft.base_unit}
                onChange={(e) => setField('base_unit', e.target.value)}
              >
                {(units ?? []).map((u) => (
                  <option key={u.id} value={u.code}>
                    {u.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Secondary unit</span>
              <select
                value={draft.secondary_unit}
                onChange={(e) => setField('secondary_unit', e.target.value)}
              >
                <option value="">— none —</option>
                {(units ?? [])
                  .filter((u) => u.code !== draft.base_unit)
                  .map((u) => (
                    <option key={u.id} value={u.code}>
                      {u.label}
                    </option>
                  ))}
              </select>
            </label>
          </div>

          <div className="panel-title">Rates per {draft.base_unit.toLowerCase()}</div>
          <div className="form-grid">
            {TIERS.map((tier) => (
              <label className="field" key={tier}>
                <span>Tier {tier}</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={draft.prices[tier]}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, prices: { ...d.prices, [tier]: e.target.value } }))
                  }
                />
              </label>
            ))}
          </div>

          <label className="checkbox">
            <input
              type="checkbox"
              checked={draft.inclusive_of_tax}
              onChange={(e) => setField('inclusive_of_tax', e.target.checked)}
            />
            Rates already include GST
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={draft.is_active}
              onChange={(e) => setField('is_active', e.target.checked)}
            />
            Available to order
          </label>

          <p className="hint">
            The item code is the catalog&apos;s key and the importer matches on it, so it
            is not editable here. A blank rate leaves that tier as it was.
          </p>
          </form>
        )}
      </Drawer>

      <BulkTranslate
        open={Boolean(bulk)}
        entityType="product"
        label="products"
        selectedIds={bulk?.ids ?? null}
        onClose={() => setBulk(null)}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ['products'] })
          queryClient.invalidateQueries({ queryKey: ['translation-pending'] })
          setPicked({})
        }}
      />

      <Confirm
        open={Boolean(confirming)}
        title={`Delete ${confirming?.item_code ?? ''}?`}
        message="Its prices and learned wordings go with it. No order has ever named it, so nothing else changes."
        busy={remove.isPending}
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          remove.mutate(confirming.id)
          setConfirming(null)
        }}
      />

      {isLoading && <Loading />}
      {isError && <ErrorNote message="Could not load products" onRetry={refetch} />}
      {data && !data.items.length && <Empty title="No products match" />}

      {data && data.items.length > 0 && (
        <>
          <table className="table">
            <thead>
              <tr>
                {SHOW_BULK_TRANSLATE && editable && (
                  <th className="tick">
                    <input
                      type="checkbox"
                      aria-label="Select every product on this page"
                      checked={pageIds.length > 0 && pageIds.every((id) => picked[id])}
                      onChange={(e) =>
                        setPicked((p) => ({
                          ...p,
                          ...Object.fromEntries(
                            pageIds.map((id) => [id, e.target.checked]),
                          ),
                        }))
                      }
                    />
                  </th>
                )}
                <th>Item</th>
                <th>Tamil</th>
                <th>Code</th>
                <th>Category</th>
                <th>HSN</th>
                <th>Unit</th>
                <th>GST</th>
                {TIERS.map((t) => (
                  <th key={t} className="right">
                    {t}
                  </th>
                ))}
                {editable && <th />}
              </tr>
            </thead>
            <tbody>
              {data.items.map((product) => (
                <tr key={product.id} className={product.is_active ? '' : 'inactive'}>
                  {SHOW_BULK_TRANSLATE && editable && (
                    <td className="tick">
                      <input
                        type="checkbox"
                        aria-label={`Select ${product.item_name}`}
                        checked={Boolean(picked[product.id])}
                        onChange={(e) =>
                          setPicked((p) => ({ ...p, [product.id]: e.target.checked }))
                        }
                      />
                    </td>
                  )}
                  <td className="strong">
                    {product.item_name}
                    {product.item_name_ta && (
                      <span className="ta-line" lang="ta">{product.item_name_ta}</span>
                    )}
                  </td>
                  <td>
                    {product.item_name_ta ? (
                      <span className="ta-status done">✓</span>
                    ) : (
                      <span className="ta-status missing">—</span>
                    )}
                  </td>
                  <td className="mono">{product.item_code}</td>
                  <td>{product.category ?? '—'}</td>
                  <td className="mono">{product.hsn ?? '—'}</td>
                  <td>
                    {product.base_unit.toLowerCase()}
                    {product.secondary_unit && (
                      <span className="muted"> / {product.secondary_unit.toLowerCase()}</span>
                    )}
                  </td>
                  <td>
                    {Number(product.tax_rate)}%
                    {/* Inclusive SKUs are billed differently — worth seeing here. */}
                    <span className="muted"> {product.inclusive_of_tax ? 'incl' : 'excl'}</span>
                  </td>
                  {TIERS.map((t) => (
                    <td key={t} className="right">
                      {rateFor(product, t)}
                    </td>
                  ))}
                  {editable && (
                    <td className="right nowrap">
                      <div className="row-actions">
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => edit(product)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          // A product on an order cannot go: its history
                          // points at it. The count says why before the
                          // click rather than after.
                          disabled={
                            product.order_line_count > 0 ||
                            remove.variables === product.id
                          }
                          title={
                            product.order_line_count > 0
                              ? `On ${product.order_line_count} order line(s) — switch it off instead`
                              : undefined
                          }
                          onClick={() => {
                            setNotice('')
                            setConfirming(product)
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          <Pagination
            page={data.page}
            pageSize={data.page_size}
            total={data.total}
            onChange={setPage}
          />
        </>
      )}
    </div>
  )
}
